// src/components/LevelOneStatusPanel.jsx
// レベル1 (display_order 最小) 術式の自立状況を「人 × 術式」マトリクスで表示
// - 1列目: スタッフ名
// - ヘッダー: レベル1 の各術式
// - セル: 達成済み (緑) / 未達成 (灰) / 該当外
// - 最後の列: 達成率 (色付き)
// - 年次フィルタ

import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';

const LevelOneStatusPanel = ({ demoData = null }) => {
  const { currentOrganization } = useOrganization();
  const [staffList, setStaffList] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [levels, setLevels] = useState([]);
  const [independence, setIndependence] = useState([]);
  const [filterYear, setFilterYear] = useState('all');
  const [filterLevelId, setFilterLevelId] = useState(null); // null = 最初のレベル自動
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (demoData) {
      setStaffList(demoData.staffList || []);
      setProcedures(demoData.procedures || []);
      setLevels(demoData.levels || []);
      setIndependence(demoData.independence || []);
      return;
    }
    if (!currentOrganization?.id) return;
    (async () => {
      setLoading(true);
      try {
        const [stfRes, prcRes, lvRes, indRes] = await Promise.all([
          supabase.from('staff').select('id, employee_id, last_name, first_name, year')
            .eq('organization_id', currentOrganization.id),
          supabase.from('procedures').select('id, name, department_id, level_id')
            .eq('organization_id', currentOrganization.id)
            .order('name'),
          supabase.from('procedure_levels').select('id, name, color, display_order')
            .eq('organization_id', currentOrganization.id)
            .order('display_order'),
          supabase.from('staff_independence_records')
            .select('id, staff_id, procedure_id, role, recognized_date')
            .eq('organization_id', currentOrganization.id),
        ]);
        setStaffList(stfRes.data || []);
        setProcedures(prcRes.data || []);
        setLevels(lvRes.data || []);
        setIndependence(indRes.data || []);
      } catch (e) {
        console.error('レベル状況取得失敗:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentOrganization?.id, demoData]);

  const targetLevel = useMemo(() => {
    if (filterLevelId) return levels.find((lv) => lv.id === filterLevelId) || levels[0];
    return levels[0];
  }, [levels, filterLevelId]);

  const targetProcedures = useMemo(() => {
    if (!targetLevel) return [];
    return procedures.filter((p) => p.level_id === targetLevel.id);
  }, [procedures, targetLevel]);

  const indSet = useMemo(() => {
    const s = new Set();
    independence.forEach((r) => s.add(`${r.staff_id}__${r.procedure_id}`));
    return s;
  }, [independence]);

  const filteredStaff = useMemo(() =>
    staffList.filter((s) => filterYear === 'all' || String(s.year || '') === String(filterYear))
      .sort((a, b) => {
        // 達成率降順
        const ratioOf = (s) => targetProcedures.length === 0 ? 0
          : targetProcedures.filter((p) => indSet.has(`${s.id}__${p.id}`)).length / targetProcedures.length;
        return ratioOf(b) - ratioOf(a);
      }),
    [staffList, filterYear, targetProcedures, indSet]);

  const uniqueYears = useMemo(() =>
    [...new Set(staffList.map((s) => s.year).filter(Boolean))].sort((a, b) => b - a),
    [staffList]);

  const colCount = targetProcedures.length;
  const totalAchievedCount = filteredStaff.reduce((sum, s) =>
    sum + targetProcedures.filter((p) => indSet.has(`${s.id}__${p.id}`)).length, 0);
  const totalCells = filteredStaff.length * colCount;
  const overallRate = totalCells > 0 ? Math.round((totalAchievedCount / totalCells) * 100) : 0;

  if (!targetLevel) {
    return (
      <div style={emptyBox}>
        ℹ️ レベルが登録されていません。 /procedure-master → 「レベル管理」から作成してください。
      </div>
    );
  }
  if (targetProcedures.length === 0) {
    return (
      <div style={emptyBox}>
        ℹ️ 「{targetLevel.name}」に紐づく術式がありません。 /procedure-master で術式にレベルを設定してください。
      </div>
    );
  }

  return (
    <div style={{
      background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
          🌱 「{targetLevel.name}」自立進捗マトリクス
          <span style={{ marginLeft: 12, fontSize: 12, color: '#64748b', fontWeight: 400 }}>
            {colCount} 術式 × {filteredStaff.length} 名 / 全体達成率 <strong style={{ color: overallRate >= 80 ? '#16a34a' : overallRate >= 50 ? '#ca8a04' : '#dc2626' }}>{overallRate}%</strong>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <select value={filterLevelId || targetLevel.id}
            onChange={(e) => setFilterLevelId(e.target.value)} style={selStyle}>
            {levels.map((lv) => <option key={lv.id} value={lv.id}>{lv.name}</option>)}
          </select>
          <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} style={selStyle}>
            <option value="all">全年次</option>
            {uniqueYears.map((y) => <option key={y} value={y}>{y}年入職</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ color: '#94a3b8', padding: 20, textAlign: 'center' }}>読み込み中...</div>
      ) : filteredStaff.length === 0 ? (
        <div style={{ color: '#94a3b8', padding: 20, textAlign: 'center', fontSize: 13 }}>
          該当スタッフがいません
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
              <tr>
                <th style={{ ...thSticky, left: 0 }}>スタッフ</th>
                <th style={thSmall}>年次</th>
                {targetProcedures.map((p) => (
                  <th key={p.id} style={{ ...thSmall, minWidth: 60 }} title={p.name}>
                    <div style={{
                      writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                      maxHeight: 110, fontSize: 11, padding: '6px 0',
                    }}>{p.name}</div>
                  </th>
                ))}
                <th style={{ ...thSticky, right: 0, background: '#eff6ff', color: '#1d4ed8' }}>達成率</th>
              </tr>
            </thead>
            <tbody>
              {filteredStaff.map((s) => {
                const achievedProcs = targetProcedures.filter((p) => indSet.has(`${s.id}__${p.id}`));
                const pct = colCount > 0 ? Math.round((achievedProcs.length / colCount) * 100) : 0;
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ ...tdSticky, left: 0, fontWeight: 600, background: 'white' }}>
                      {s.last_name} {s.first_name}
                    </td>
                    <td style={{ ...tdSmall, color: '#64748b' }}>{s.year || '-'}</td>
                    {targetProcedures.map((p) => {
                      const done = indSet.has(`${s.id}__${p.id}`);
                      return (
                        <td key={p.id} style={{
                          ...tdSmall,
                          background: done ? '#dcfce7' : '#fef2f2',
                          color: done ? '#16a34a' : '#dc2626',
                          fontWeight: 700,
                        }} title={p.name}>
                          {done ? '✓' : '–'}
                        </td>
                      );
                    })}
                    <td style={{
                      ...tdSticky, right: 0,
                      background: pct === 100 ? '#dcfce7' : pct >= 50 ? '#fef9c3' : '#fee2e2',
                      color: pct === 100 ? '#16a34a' : pct >= 50 ? '#ca8a04' : '#dc2626',
                      fontWeight: 700,
                    }}>
                      {achievedProcs.length}/{colCount} ({pct}%)
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot style={{ background: '#f1f5f9' }}>
              <tr>
                <td style={{ ...tdSticky, left: 0, fontWeight: 700, background: '#f1f5f9' }}>合計</td>
                <td style={tdSmall}></td>
                {targetProcedures.map((p) => {
                  const achievedByProc = filteredStaff.filter((s) => indSet.has(`${s.id}__${p.id}`)).length;
                  const procPct = filteredStaff.length > 0 ? Math.round((achievedByProc / filteredStaff.length) * 100) : 0;
                  return (
                    <td key={p.id} style={{ ...tdSmall, fontWeight: 700, color: '#475569' }} title={`${achievedByProc}/${filteredStaff.length}`}>
                      {procPct}%
                    </td>
                  );
                })}
                <td style={{
                  ...tdSticky, right: 0, background: '#eff6ff', color: '#1d4ed8', fontWeight: 700,
                }}>
                  {overallRate}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

const selStyle = { padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 };
const thSticky = {
  padding: 8, textAlign: 'center', fontWeight: 700, color: '#475569', fontSize: 11,
  position: 'sticky', background: 'white', border: '1px solid #e5e7eb', zIndex: 2,
};
const thSmall = { padding: 6, textAlign: 'center', fontWeight: 700, color: '#475569', fontSize: 11, border: '1px solid #e5e7eb' };
const tdSticky = {
  padding: '6px 10px', textAlign: 'center', fontSize: 12,
  position: 'sticky', border: '1px solid #e5e7eb', zIndex: 1,
};
const tdSmall = { padding: 6, textAlign: 'center', fontSize: 11, border: '1px solid #e5e7eb' };
const emptyBox = {
  background: 'white', borderRadius: 12, padding: 20,
  border: '1px solid #e5e7eb', color: '#94a3b8', fontSize: 13,
};

export default LevelOneStatusPanel;
